import { CheckCircle2, Clock, Phone, Calendar } from "lucide-react";
import { isWithinBusinessHours, BUSINESS_HOURS_LABEL } from "@/lib/businessHours";

interface WhatHappensNextProps {
  /** How the lead asked to be contacted. */
  channel: "call" | "text";
  /** Optional className for the wrapper. */
  className?: string;
  /** Compact spacing (use inside dense success cards like the hero micro-form). */
  compact?: boolean;
}

/**
 * Post-submit "What happens next" timeline. Shown in success states across
 * HeroMicroForm and QuickQuoteForm so visitors get an explicit, predictable
 * picture of the next 24 hours.
 *
 * Step copy adapts to whether the visitor submitted during business hours.
 */
const WhatHappensNext = ({ channel, className = "", compact = false }: WhatHappensNextProps) => {
  const inHours = isWithinBusinessHours();
  const channelLabel = channel === "text" ? "text" : "call";

  const steps = [
    {
      icon: CheckCircle2,
      title: "Just now",
      body: "Request received — your spot is locked in our queue.",
      done: true,
    },
    {
      icon: Clock,
      title: inHours ? "Within 2 hours" : "Next business morning",
      body: inHours
        ? "A real fence expert reviews your project (no bots, no callcenter)."
        : `We open ${BUSINESS_HOURS_LABEL} — your project gets reviewed first thing.`,
      done: false,
    },
    {
      icon: channel === "text" ? Phone : Phone,
      title: inHours ? "Within 1 business day" : "Calls returned during business hours",
      body: `We ${channelLabel} you with a fair price range and answer any questions.`,
      done: false,
    },
    {
      icon: Calendar,
      title: "When you're ready",
      body: "Pick a time for a free virtual walk-through — only if it feels like a fit.",
      done: false,
    },
  ];

  return (
    <ol
      className={`text-left ${compact ? "space-y-2.5" : "space-y-3"} ${className}`}
      aria-label="What happens next"
    >
      {steps.map((step, i) => {
        const Icon = step.icon;
        const isLast = i === steps.length - 1;
        return (
          <li key={i} className="relative flex gap-3">
            {/* Connector line */}
            {!isLast && (
              <span
                aria-hidden="true"
                className={`absolute left-[14px] top-7 w-px ${
                  compact ? "h-[calc(100%-12px)]" : "h-[calc(100%-8px)]"
                } ${step.done ? "bg-primary/40" : "bg-border"}`}
              />
            )}
            <span
              className={`relative z-10 flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center ${
                step.done
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground border border-border"
              }`}
            >
              <Icon className="w-3.5 h-3.5" aria-hidden="true" />
            </span>
            <div className="flex-1 pt-0.5">
              <p
                className={`${
                  compact ? "text-xs" : "text-sm"
                } font-semibold text-foreground leading-tight`}
              >
                {step.title}
              </p>
              <p
                className={`${
                  compact ? "text-[11px]" : "text-xs"
                } text-muted-foreground mt-0.5 leading-snug`}
              >
                {step.body}
              </p>
            </div>
          </li>
        );
      })}
    </ol>
  );
};

export default WhatHappensNext;
